import type { StatusTone } from '@/lib/status-tones'
import type {
  StockCondition,
  StockPackaging,
  UsedGrade,
  WarehouseItem,
  WarehouseLot,
  WarehouseStockEvent,
} from '@/api/warehouse'

export const CONDITION_OPTIONS: ReadonlyArray<{ value: StockCondition; label: string }> = [
  { value: 'new', label: 'New' },
  { value: 'open_box', label: 'Open box' },
  { value: 'scratch_dent', label: 'Scratch & dent' },
  { value: 'used', label: 'Used' },
]

export const GRADE_OPTIONS: ReadonlyArray<{ value: UsedGrade; label: string }> = [
  { value: 'like_new', label: 'Like new' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
]

export const PACKAGING_OPTIONS: ReadonlyArray<{ value: StockPackaging; label: string }> = [
  { value: 'boxed', label: 'Boxed' },
  { value: 'crated', label: 'Crated' },
  { value: 'palletized', label: 'Palletized' },
  { value: 'loose', label: 'Loose' },
]

const CONDITION_TONES: Record<StockCondition, StatusTone> = {
  new: 'success',
  open_box: 'progress',
  scratch_dent: 'warning',
  used: 'neutral',
}

export function conditionTone(condition: StockCondition): StatusTone {
  return CONDITION_TONES[condition]
}

/** "Scratch & dent", "Used (Good)". */
export function conditionLabel(condition: StockCondition, grade?: UsedGrade | null) {
  const base = CONDITION_OPTIONS.find((option) => option.value === condition)?.label ?? condition
  const gradeLabel = grade ? GRADE_OPTIONS.find((option) => option.value === grade)?.label : null
  return condition === 'used' && gradeLabel ? `${base} (${gradeLabel})` : base
}

export function packagingLabel(packaging: StockPackaging | null | undefined) {
  return PACKAGING_OPTIONS.find((option) => option.value === packaging)?.label ?? null
}

/** "Bay B4 · Pallet P12", or null when neither is recorded. */
export function locationLabel(stock: Pick<WarehouseLot, 'bay' | 'pallet'>) {
  const parts = [stock.bay && `Bay ${stock.bay}`, stock.pallet && `Pallet ${stock.pallet}`].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

/**
 * Stock counts single units. For a product the catalog sells by the case, say so next to the count:
 * "36 units" and "3 cases of 12"; anything else is just the number.
 */
export function quantityLabel(quantity: number, unitsPerCase: number) {
  if (unitsPerCase <= 1) return String(quantity)
  return `${quantity} ${quantity === 1 ? 'unit' : 'units'}`
}

export function casesNote(quantity: number, unitsPerCase: number) {
  if (unitsPerCase <= 1) return null
  const cases = Math.floor(quantity / unitsPerCase)
  const loose = quantity % unitsPerCase
  if (!cases) return `Case of ${unitsPerCase}`
  const caseText = `${cases} ${cases === 1 ? 'case' : 'cases'} of ${unitsPerCase}`
  return loose ? `${caseText} + ${loose}` : caseText
}

export function stockStatusPill(stock: Pick<WarehouseLot, 'status'>): { tone: StatusTone; label: string } | null {
  if (stock.status === 'depleted') return { tone: 'neutral', label: 'Sold out' }
  if (stock.status === 'removed') return { tone: 'danger', label: 'Removed' }
  return null
}

const EVENT_VERBS: Record<WarehouseStockEvent['kind'], string> = {
  received: 'Received',
  adjusted: 'Count changed',
  moved: 'Moved',
  condition_changed: 'Condition changed',
  reserved: 'Reserved',
  released: 'Released',
  shipped: 'Shipped',
  removed: 'Removed',
  imported: 'Imported from the store',
}

type Place = { bay?: string | null; pallet?: string | null }

function placeText(place: Place | undefined) {
  if (!place) return 'no bay'
  return locationLabel({ bay: place.bay ?? null, pallet: place.pallet ?? null }) ?? 'no bay'
}

function signed(value: number) {
  return value > 0 ? `+${value}` : String(value)
}

/** One history line: what happened, in staff words. */
export function describeEvent(event: WarehouseStockEvent): { title: string; detail: string | null } {
  const details = (event.details ?? {}) as {
    from?: Place & { condition?: StockCondition; used_grade?: UsedGrade | null }
    to?: Place & { condition?: StockCondition; used_grade?: UsedGrade | null }
  }
  const verb = EVENT_VERBS[event.kind]
  switch (event.kind) {
    case 'received':
    case 'imported':
      return { title: `${verb} ${event.quantity_delta}`, detail: event.note ?? null }
    case 'adjusted':
    case 'removed':
      return { title: `${verb} ${signed(event.quantity_delta)}`, detail: event.note ?? null }
    case 'moved':
      // A split moves only some units: the count says how many left or arrived.
      return {
        title: event.quantity_delta ? `${verb} ${Math.abs(event.quantity_delta)}` : verb,
        detail: `${placeText(details.from)} → ${placeText(details.to)}`,
      }
    case 'condition_changed':
      return {
        title: verb,
        detail:
          details.from?.condition && details.to?.condition
            ? `${conditionLabel(details.from.condition, details.from.used_grade)} → ${conditionLabel(details.to.condition, details.to.used_grade)}`
            : null,
      }
    case 'reserved':
    case 'released':
    case 'shipped':
      return { title: `${verb} ${Math.abs(event.reserved_delta)}`, detail: event.note ?? null }
  }
}

/** Where an item's page lives: one product in one condition. */
export function itemLink(item: Pick<WarehouseItem, 'condition' | 'used_grade'> & { product: { uuid: string } }) {
  return {
    to: '/warehouse/item/$productId' as const,
    params: { productId: item.product.uuid },
    search: { condition: item.condition, grade: item.used_grade ?? undefined },
  }
}

/** "$2,790.00", or "$2,400.00 – $2,790.00" when the receipts were bought at different prices. */
export function netRange(lots: ReadonlyArray<Pick<WarehouseLot, 'unit_net'>>, format: (value: number) => string) {
  const values = lots.map((lot) => lot.unit_net).filter((value) => value != null).map(Number)
  if (!values.length) return null
  const low = Math.min(...values)
  const high = Math.max(...values)
  return low === high ? format(low) : `${format(low)} – ${format(high)}`
}
